import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './CompanyDetail.css';
import API_URL from '../config';

function CompanyDetail() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlate, setEditingPlate] = useState(null);
  const [newPlate, setNewPlate] = useState({ name: '', google_review_url: '' });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: '', last_name: '', email: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [revokeAdminTarget, setRevokeAdminTarget] = useState(null); // { id, email, name }
  const [revokeAdminLoading, setRevokeAdminLoading] = useState(false);
  const [revokeAdminResult, setRevokeAdminResult] = useState(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoResult, setInfoResult] = useState(null);
  const [copiedPlateId, setCopiedPlateId] = useState(null);
  const [plateAccessMap, setPlateAccessMap] = useState({}); // { userId: Set<plateId> }
  const [plateAccessSaving, setPlateAccessSaving] = useState({}); // { userId: bool }
  const [plateAccessOpen, setPlateAccessOpen] = useState(null); // userId of open dropdown
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchCompanies();
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin) return;
    setActiveTab('info');
    setCompany(null);
    setMembers([]);
    setInvitations([]);
    setLoading(true);
    setImportResult(null);
    fetchCompanyDetail();
    fetchMembers();
    fetchCompanies();
  }, [user, id]);

  useEffect(() => {
    if (!plateAccessOpen) return;
    const handleClickOutside = () => setPlateAccessOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [plateAccessOpen]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setCompanies(await response.json());
    } catch (error) {
      console.error('Erreur companies:', error);
    }
  };

  const fetchCompanyDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/companies/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInfo = (company) => {
    setInfoForm({
      name: company.name,
      owner_first_name: company.owner.first_name || '',
      owner_last_name: company.owner.last_name || '',
      owner_email: company.owner.email || '',
    });
    setInfoResult(null);
    setEditingInfo(true);
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    setInfoSaving(true);
    setInfoResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/companies/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(infoForm)
      });
      const data = await res.json();
      if (res.ok) {
        setInfoResult({ success: true, message: 'Informations mises à jour' });
        fetchCompanyDetail();
        setTimeout(() => { setEditingInfo(false); setInfoResult(null); }, 1200);
      } else {
        setInfoResult({ success: false, message: data.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch {
      setInfoResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setInfoSaving(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [membersRes, invitesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/companies/${id}/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/companies/${id}/invitations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setMembers(membersData);
        // Load plate access for each member
        const accessMap = {};
        await Promise.all(membersData.map(async (m) => {
          try {
            const res = await fetch(`${API_URL}/api/admin/companies/${id}/members/${m.id}/plates`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              accessMap[m.id] = new Set(data.plate_ids);
            } else {
              accessMap[m.id] = new Set();
            }
          } catch {
            accessMap[m.id] = new Set();
          }
        }));
        setPlateAccessMap(accessMap);
      }
      if (invitesRes.ok) setInvitations(await invitesRes.json());
    } catch (error) {
      console.error('Erreur membres:', error);
    }
  };

  const handleSavePlateAccess = async (userId) => {
    setPlateAccessSaving(s => ({ ...s, [userId]: true }));
    try {
      const token = localStorage.getItem('token');
      const plateIds = Array.from(plateAccessMap[userId] || []);
      const res = await fetch(`${API_URL}/api/admin/companies/${id}/members/${userId}/plates`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plate_ids: plateIds })
      });
      if (res.ok) {
        const data = await res.json();
        setPlateAccessMap(m => ({ ...m, [userId]: new Set(data.plate_ids) }));
      }
    } catch (err) {
      console.error('Erreur sauvegarde accès plaques:', err);
    } finally {
      setPlateAccessSaving(s => ({ ...s, [userId]: false }));
      setPlateAccessOpen(null);
    }
  };

  const togglePlateForUser = (userId, plateId) => {
    setPlateAccessMap(m => {
      const current = new Set(m[userId] || []);
      if (current.has(plateId)) current.delete(plateId);
      else current.add(plateId);
      return { ...m, [userId]: current };
    });
  };

  const handleCreatePlate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingPlate) {
        const response = await fetch(`${API_URL}/api/admin/nfc-plates/${editingPlate.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newPlate)
        });
        if (response.ok) { setShowModal(false); setEditingPlate(null); setNewPlate({ name: '', google_review_url: '' }); fetchCompanyDetail(); }
      } else {
        const response = await fetch(`${API_URL}/api/admin/companies/${id}/nfc-plates`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newPlate)
        });
        if (response.ok) { setShowModal(false); setNewPlate({ name: '', google_review_url: '' }); fetchCompanyDetail(); }
      }
    } catch (error) {
      console.error('Erreur création/modification plaque:', error);
    }
  };

  const handleEditPlate = (plate) => {
    setEditingPlate(plate);
    setNewPlate({ name: plate.name, google_review_url: plate.google_review_url });
    setShowModal(true);
  };

  const handleDeletePlate = async (plateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette plaque ?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/nfc-plates/${plateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) fetchCompanyDetail();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleImportXLSX = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/admin/companies/${id}/import-plates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (response.ok) { setImportResult({ success: true, ...data }); fetchCompanyDetail(); }
      else setImportResult({ success: false, message: data.detail || "Erreur lors de l'import" });
    } catch {
      setImportResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setImporting(false);
    }
  };

  const handleExportXLSX = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/companies/${id}/export-plates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        a.download = (disposition && disposition.includes('filename='))
          ? disposition.split('filename=')[1].replace(/"/g, '')
          : 'plaques.xlsx';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json().catch(() => ({}));
        setImportResult({ success: false, message: data.detail || 'Erreur lors de l\'export' });
      }
    } catch (err) {
      console.error('Erreur export:', err);
      setImportResult({ success: false, message: 'Erreur réseau lors de l\'export' });
    } finally { setExporting(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/companies/${id}/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteForm.email.trim(),
          first_name: inviteForm.first_name.trim() || null,
          last_name: inviteForm.last_name.trim() || null
        })
      });
      const data = await response.json();
      if (response.ok) {
        setInviteResult({ success: true, message: `Invitation envoyée à ${inviteForm.email}` });
        setInviteForm({ first_name: '', last_name: '', email: '' });
        fetchMembers();
        setTimeout(() => { setShowInviteModal(false); setInviteResult(null); }, 1500);
      } else {
        setInviteResult({ success: false, message: data.detail || 'Erreur' });
      }
    } catch {
      setInviteResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Retirer ce membre de l\'entreprise ?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/companies/${id}/members/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) fetchMembers();
    } catch (err) { console.error(err); }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) fetchMembers();
    } catch (err) { console.error(err); }
  };

  const handleRevokeAdmin = async () => {
    if (!revokeAdminTarget) return;
    setRevokeAdminLoading(true);
    setRevokeAdminResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/app-admins/${revokeAdminTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setRevokeAdminResult({ success: true, message: data.message });
        fetchMembers();
        setTimeout(() => { setRevokeAdminTarget(null); setRevokeAdminResult(null); }, 1500);
      } else {
        setRevokeAdminResult({ success: false, message: data.detail || 'Erreur lors de la révocation' });
      }
    } catch (err) {
      setRevokeAdminResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setRevokeAdminLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="hamburger-button" onClick={() => setMenuOpen(!menuOpen)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path d="M20 7L4 7" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              <path d="M20 12L4 12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              <path d="M20 17L4 17" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
            </g>
          </svg>
        </button>
        <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="mobile-logo" />
        <div className="mobile-spacer"></div>
      </div>

      <aside className={`sidebar ${menuOpen ? 'menu-open' : ''}`}>
        <div className="sidebar-mobile-header">
          <button className="close-menu-button" onClick={() => setMenuOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
              <path d="M6 6L18 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="sidebar-mobile-logo" />
          <div className="mobile-spacer"></div>
        </div>

        <Link to="/" className="sidebar-logo">
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" />
        </Link>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item active">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path d="M22 22L2 22" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M21 22V6C21 4.11438 21 3.17157 20.4143 2.58579C19.8285 2 18.8857 2 17 2H15C13.1144 2 12.1716 2 11.5858 2.58579C11.1143 3.05733 11.0223 3.76022 11.0044 5" stroke="#1C274C" strokeWidth="1.5"></path>
                <path d="M15 22V9C15 7.11438 15 6.17157 14.4142 5.58579C13.8284 5 12.8856 5 11 5H7C5.11438 5 4.17157 5 3.58579 5.58579C3 6.17157 3 7.11438 3 9V22" stroke="#1C274C" strokeWidth="1.5"></path>
                <path d="M9 22V19" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 8H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 11H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
                <path d="M6 14H12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              </g>
            </svg>
            <span>Entreprises</span>
          </Link>

          <div className="sidebar-companies">
            {companies.map(c => (
              <div key={c.id} className="sidebar-company-accordion">
                <button
                  className={`sidebar-company-header${c.id === parseInt(id) ? ' active' : ''}`}
                  onClick={() => navigate(`/admin/company/${c.id}`)}
                >
                  <span>{c.name}</span>
                  <svg className={`company-chevron${c.id === parseInt(id) ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {c.id === parseInt(id) && company && (
                  <div className="sidebar-subnav-items">
                    <button
                      className={`sidebar-subnav-item${activeTab === 'info' ? ' active' : ''}`}
                      onClick={() => setActiveTab('info')}
                    >
                      Informations
                    </button>
                    <button
                      className={`sidebar-subnav-item${activeTab === 'plates' ? ' active' : ''}`}
                      onClick={() => setActiveTab('plates')}
                    >
                      Plaques NFC
                      <span className="subnav-count">{company.nfc_plates.length}</span>
                    </button>
                    <button
                      className={`sidebar-subnav-item${activeTab === 'members' ? ' active' : ''}`}
                      onClick={() => setActiveTab('members')}
                    >
                      Membres
                      <span className="subnav-count">{members.length + invitations.length}</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Link to="/admin/users" className="nav-item">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M12.0001 1.25C9.37678 1.25 7.25013 3.37665 7.25013 6C7.25013 8.62335 9.37678 10.75 12.0001 10.75C14.6235 10.75 16.7501 8.62335 16.7501 6C16.7501 3.37665 14.6235 1.25 12.0001 1.25ZM8.75013 6C8.75013 4.20507 10.2052 2.75 12.0001 2.75C13.7951 2.75 15.2501 4.20507 15.2501 6C15.2501 7.79493 13.7951 9.25 12.0001 9.25C10.2052 9.25 8.75013 7.79493 8.75013 6Z" fill="#1C274C"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M12.0001 12.25C9.68658 12.25 7.55506 12.7759 5.97558 13.6643C4.41962 14.5396 3.25013 15.8661 3.25013 17.5L3.25007 17.602C3.24894 18.7638 3.24752 20.222 4.52655 21.2635C5.15602 21.7761 6.03661 22.1406 7.22634 22.3815C8.4194 22.6229 9.97436 22.75 12.0001 22.75C14.0259 22.75 15.5809 22.6229 16.7739 22.3815C17.9637 22.1406 18.8443 21.7761 19.4737 21.2635C20.7527 20.222 20.7513 18.7638 20.7502 17.602L20.7501 17.5C20.7501 15.8661 19.5807 14.5396 18.0247 13.6643C16.4452 12.7759 14.3137 12.25 12.0001 12.25ZM4.75013 17.5C4.75013 16.6487 5.37151 15.7251 6.71098 14.9717C8.02693 14.2315 9.89541 13.75 12.0001 13.75C14.1049 13.75 15.9733 14.2315 17.2893 14.9717C18.6288 15.7251 19.2501 16.6487 19.2501 17.5C19.2501 18.8078 19.2098 19.544 18.5265 20.1004C18.156 20.4022 17.5366 20.6967 16.4763 20.9113C15.4194 21.1252 13.9744 21.25 12.0001 21.25C10.0259 21.25 8.58087 21.1252 7.52393 20.9113C6.46366 20.6967 5.84425 20.4022 5.47372 20.1004C4.79045 19.544 4.75013 18.8078 4.75013 17.5Z" fill="#1C274C"/>
            </svg>
            <span>Utilisateurs</span>
          </Link>
        </nav>

        <div className="sidebar-user">
          <div className="user-avatar">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent((user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email?.split('@')[0]) || 'A')}&background=fbd900&color=000&bold=true`}
              alt="Avatar"
            />
          </div>
          <div className="user-info">
            <div className="user-name">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.email?.split('@')[0] || 'Administrateur'}
            </div>
            <div className="user-role">Administrateur</div>
          </div>
          <button className="logout-button" onClick={logout} title="Déconnexion">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {loading && <div className="loading-state" style={{padding:'3rem',textAlign:'center'}}>Chargement...</div>}
        {!loading && !company && <div className="loading-state" style={{padding:'3rem',textAlign:'center'}}>Entreprise introuvable</div>}
        {!loading && company && <React.Fragment>
        <div className="page-header">
          <div className="page-header-left">
            <h1>
              {activeTab === 'info' ? company.name
                : activeTab === 'plates' ? 'Plaques NFC'
                : 'Équipe'}
            </h1>
            <p>
              {activeTab === 'info' ? 'Informations globales de l\'entreprise'
                : activeTab === 'plates' ? 'Gérez les plaques NFC de cette entreprise'
                : 'Gérez les collaborateurs de cette entreprise'}
            </p>
          </div>
          <div className="page-header-actions">
            {activeTab === 'info' && (
              <Link to={`/admin/company/${id}/dashboard`} className="btn-view-dashboard">
                Voir le dashboard
              </Link>
            )}
            {activeTab === 'plates' && (
              <>
                <button className="new-plate-button" onClick={() => setShowModal(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/>
                    <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/>
                    <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/>
                    <path d="M16.37 2a20.16 20.16 0 0 1 0 20"/>
                  </svg>
                  <span>Nouvelle plaque</span>
                </button>
                <label className={`btn-import ${importing ? 'btn-loading' : ''}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  {importing ? 'Import...' : 'Importer XLSX'}
                  <input type="file" accept=".xlsx,.xlsm" style={{ display: 'none' }} onChange={handleImportXLSX} disabled={importing} />
                </label>
                <button className={`btn-export ${exporting ? 'btn-loading' : ''}`} onClick={handleExportXLSX} disabled={exporting}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {exporting ? 'Export...' : 'Exporter XLSX'}
                </button>
              </>
            )}
            {activeTab === 'members' && (
              <button className="btn-invite-open" onClick={() => setShowInviteModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                Inviter
              </button>
            )}
          </div>
        </div>

        {importResult && (
          <div className={`import-result ${importResult.success ? 'import-success' : 'import-error'}`} style={{marginBottom:'1rem'}}>
            <div>
              <span>{importResult.message}</span>
              {importResult.errors && importResult.errors.length > 0 && (
                <ul className="import-errors-list">{importResult.errors.map((err, i) => <li key={i}>{err}</li>)}</ul>
              )}
            </div>
            <button className="import-result-close" onClick={() => setImportResult(null)}>×</button>
          </div>
        )}

        {/* Tab: Informations */}
        {activeTab === 'info' && (
          <div className="company-info-card">
            {!editingInfo ? (
              <>
                <div className="info-row">
                  <span className="info-label">Nom de l'entreprise</span>
                  <span className="info-value">{company.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Propriétaire</span>
                  <span className="info-value">{company.owner.first_name} {company.owner.last_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email</span>
                  <span className="info-value">{company.owner.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Utilisateurs</span>
                  <span className="users-badge">{company.users_count}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Création</span>
                  <span className="info-value">{formatDate(company.created_at)}</span>
                </div>
                <div style={{ marginTop: '1.25rem' }}>
                  <button className="btn-submit" style={{ padding: '0.6rem 1.25rem', fontSize: '0.875rem' }} onClick={() => handleEditInfo(company)}>
                    Modifier
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSaveInfo}>
                <div className="info-row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span className="info-label">Nom de l'entreprise</span>
                  <input className="info-edit-input" value={infoForm.name} onChange={e => setInfoForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="info-row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span className="info-label">Prénom du propriétaire</span>
                  <input className="info-edit-input" value={infoForm.owner_first_name} onChange={e => setInfoForm(f => ({ ...f, owner_first_name: e.target.value }))} />
                </div>
                <div className="info-row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span className="info-label">Nom du propriétaire</span>
                  <input className="info-edit-input" value={infoForm.owner_last_name} onChange={e => setInfoForm(f => ({ ...f, owner_last_name: e.target.value }))} />
                </div>
                <div className="info-row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span className="info-label">Email</span>
                  <input className="info-edit-input" type="email" value={infoForm.owner_email} onChange={e => setInfoForm(f => ({ ...f, owner_email: e.target.value }))} required />
                </div>
                {infoResult && (
                  <div className={`invite-modal-result ${infoResult.success ? 'invite-success' : 'invite-error'}`} style={{ marginTop: '1rem' }}>
                    {infoResult.message}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                  <button type="button" className="btn-cancel" onClick={() => { setEditingInfo(false); setInfoResult(null); }} disabled={infoSaving}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-submit" style={{ padding: '0.6rem 1.25rem', fontSize: '0.875rem' }} disabled={infoSaving}>
                    {infoSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Tab: Plaques NFC */}
        {activeTab === 'plates' && (
          <div className="plates-section">
            <div className="plates-grid">
              {company.nfc_plates.length === 0 ? (
                <div className="empty-state">Aucune plaque NFC pour cette entreprise</div>
              ) : (
                company.nfc_plates.map((plate) => (
                  <div key={plate.id} className="plate-card">
                    <div className="plate-actions">
                      <button className="icon-button edit-button" onClick={() => handleEditPlate(plate)} title="Éditer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button className="icon-button delete-icon-button" onClick={() => handleDeletePlate(plate.id)} title="Supprimer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"/>
                        </svg>
                      </button>
                    </div>
                    <div className="plate-header">
                      <div className="plate-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/>
                          <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/>
                          <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/>
                          <path d="M16.37 2a20.16 20.16 0 0 1 0 20"/>
                        </svg>
                      </div>
                      <div className="plate-header-text">
                        <h3>{plate.name}</h3>
                        {plate.plate_uid && (
                          <span className="plate-uid-badge">{plate.plate_uid}</span>
                        )}
                      </div>
                    </div>
                    <div className="plate-details">
                      <div className="detail-row">
                        <span className="detail-label">Scans</span>
                        <span className="detail-value">{plate.scan_count || 0}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Création</span>
                        <span className="detail-value">{formatDate(plate.created_at)}</span>
                      </div>
                    </div>
                    <div className="plate-link-actions">
                      <a href={`/link/${plate.unique_link}`} target="_blank" rel="noopener noreferrer" className="access-link-button">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 7h10v10"/>
                          <path d="M7 17 17 7"/>
                        </svg>
                        <span>Accéder</span>
                      </a>
                      <button
                        className={`copy-link-button${copiedPlateId === plate.id ? ' copied' : ''}`}
                        onClick={() => {
                          const text = `${window.location.origin}/link/${plate.unique_link}`;
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(text);
                          } else {
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.focus();
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }
                          setCopiedPlateId(plate.id);
                          setTimeout(() => setCopiedPlateId(null), 1000);
                        }}
                      >
                        {copiedPlateId === plate.id ? (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15.4933 6.93502C15.8053 7.20743 15.8374 7.68122 15.565 7.99325L7.70786 16.9933C7.56543 17.1564 7.35943 17.25 7.14287 17.25C6.9263 17.25 6.72031 17.1564 6.57788 16.9933L3.43502 13.3933C3.16261 13.0812 3.19473 12.6074 3.50677 12.335C3.8188 12.0626 4.29259 12.0947 4.565 12.4068L7.14287 15.3596L14.435 7.00677C14.7074 6.69473 15.1812 6.66261 15.4933 6.93502Z" fill="currentColor"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M20.5175 7.01946C20.8174 7.30513 20.829 7.77986 20.5433 8.07981L11.9716 17.0798C11.8201 17.2389 11.6065 17.3235 11.3872 17.3114C11.1679 17.2993 10.9649 17.1917 10.8318 17.0169L10.4035 16.4544C10.1526 16.1249 10.2163 15.6543 10.5458 15.4034C10.8289 15.1878 11.2161 15.2044 11.4787 15.4223L19.4571 7.04531C19.7428 6.74537 20.2175 6.73379 20.5175 7.01946Z" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15 1.25H10.9436C9.10583 1.24998 7.65019 1.24997 6.51098 1.40314C5.33856 1.56076 4.38961 1.89288 3.64124 2.64124C2.89288 3.38961 2.56076 4.33856 2.40314 5.51098C2.24997 6.65019 2.24998 8.10582 2.25 9.94357V16C2.25 17.8722 3.62205 19.424 5.41551 19.7047C5.55348 20.4687 5.81753 21.1208 6.34835 21.6517C6.95027 22.2536 7.70814 22.5125 8.60825 22.6335C9.47522 22.75 10.5775 22.75 11.9451 22.75H15.0549C16.4225 22.75 17.5248 22.75 18.3918 22.6335C19.2919 22.5125 20.0497 22.2536 20.6517 21.6517C21.2536 21.0497 21.5125 20.2919 21.6335 19.3918C21.75 18.5248 21.75 17.4225 21.75 16.0549V10.9451C21.75 9.57754 21.75 8.47522 21.6335 7.60825C21.5125 6.70814 21.2536 5.95027 20.6517 5.34835C20.1208 4.81753 19.4687 4.55348 18.7047 4.41551C18.424 2.62205 16.8722 1.25 15 1.25ZM17.1293 4.27117C16.8265 3.38623 15.9876 2.75 15 2.75H11C9.09318 2.75 7.73851 2.75159 6.71085 2.88976C5.70476 3.02502 5.12511 3.27869 4.7019 3.7019C4.27869 4.12511 4.02502 4.70476 3.88976 5.71085C3.75159 6.73851 3.75 8.09318 3.75 10V16C3.75 16.9876 4.38624 17.8265 5.27117 18.1293C5.24998 17.5194 5.24999 16.8297 5.25 16.0549V10.9451C5.24998 9.57754 5.24996 8.47522 5.36652 7.60825C5.48754 6.70814 5.74643 5.95027 6.34835 5.34835C6.95027 4.74643 7.70814 4.48754 8.60825 4.36652C9.47522 4.24996 10.5775 4.24998 11.9451 4.25H15.0549C15.8297 4.24999 16.5194 4.24998 17.1293 4.27117ZM7.40901 6.40901C7.68577 6.13225 8.07435 5.9518 8.80812 5.85315C9.56347 5.75159 10.5646 5.75 12 5.75H15C16.4354 5.75 17.4365 5.75159 18.1919 5.85315C18.9257 5.9518 19.3142 6.13225 19.591 6.40901C19.8678 6.68577 20.0482 7.07435 20.1469 7.80812C20.2484 8.56347 20.25 9.56458 20.25 11V16C20.25 17.4354 20.2484 18.4365 20.1469 19.1919C20.0482 19.9257 19.8678 20.3142 19.591 20.591C19.3142 20.8678 18.9257 21.0482 18.1919 21.1469C17.4365 21.2484 16.4354 21.25 15 21.25H12C10.5646 21.25 9.56347 21.2484 8.80812 21.1469C8.07435 21.0482 7.68577 20.8678 7.40901 20.591C7.13225 20.3142 6.9518 19.9257 6.85315 19.1919C6.75159 18.4365 6.75 17.4354 6.75 16V11C6.75 9.56458 6.75159 8.56347 6.85315 7.80812C6.9518 7.07435 7.13225 6.68577 7.40901 6.40901Z" fill="currentColor"/>
                          </svg>
                        )}
                        <span>Copier</span>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Tab: Membres */}
        {activeTab === 'members' && (
          <div className="admins-list">
            {members.map(m => {
              const displayName = (m.first_name && m.last_name)
                ? `${m.first_name} ${m.last_name}`
                : m.email.split('@')[0];
              const userPlates = plateAccessMap[m.id] || new Set();
              const companyPlates = company?.nfc_plates || [];
              const isOpen = plateAccessOpen === m.id;
              return (
                <div key={m.id} className="admin-row">
                  <div className="admin-avatar">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=fbd900&color=000&bold=true`}
                      alt="Avatar"
                    />
                  </div>
                  <div className="admin-info">
                    <div className="admin-name">{displayName}</div>
                    <div className="admin-email">{m.email}</div>
                  </div>
                  <div className="admin-meta">
                    {m.is_admin && <span className="admin-badge">Admin</span>}
                    {m.created_at && (
                      <span className="admin-date">Depuis le {formatDate(m.created_at)}</span>
                    )}
                  </div>
                  <div className="admin-actions" style={{gap:'0.5rem',display:'flex',alignItems:'center',flexWrap:'wrap'}}>
                    {companyPlates.length > 0 && (
                      <div className="plate-access-selector" style={{position:'relative'}}>
                        <button
                          className="btn-plate-access"
                          onClick={(e) => { e.stopPropagation(); setPlateAccessOpen(isOpen ? null : m.id); }}
                          title="Gérer l'accès aux plaques"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'5px'}}>
                            <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/>
                            <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/>
                            <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/>
                            <path d="M16.37 2a20.16 20.16 0 0 1 0 20"/>
                          </svg>
                          Plaques ({userPlates.size}/{companyPlates.length})
                        </button>
                        {isOpen && (
                          <div className="plate-access-dropdown" onClick={e => e.stopPropagation()}>
                            <div className="plate-access-header">
                              <span>Accès aux plaques</span>
                              <button
                                className="plate-access-toggle-all"
                                onClick={() => {
                                  if (userPlates.size === companyPlates.length) {
                                    setPlateAccessMap(mp => ({ ...mp, [m.id]: new Set() }));
                                  } else {
                                    setPlateAccessMap(mp => ({ ...mp, [m.id]: new Set(companyPlates.map(p => p.id)) }));
                                  }
                                }}
                              >
                                {userPlates.size === companyPlates.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                              </button>
                            </div>
                            <div className="plate-access-list">
                              {companyPlates.map(plate => (
                                <label key={plate.id} className="plate-access-item">
                                  <input
                                    type="checkbox"
                                    checked={userPlates.has(plate.id)}
                                    onChange={() => togglePlateForUser(m.id, plate.id)}
                                  />
                                  <span>{plate.name}</span>
                                  {plate.plate_uid && <span className="plate-uid-small">{plate.plate_uid}</span>}
                                </label>
                              ))}
                            </div>
                            <div className="plate-access-footer">
                              <button className="btn-cancel" onClick={() => setPlateAccessOpen(null)}>Annuler</button>
                              <button
                                className="btn-submit"
                                onClick={() => handleSavePlateAccess(m.id)}
                                disabled={plateAccessSaving[m.id]}
                              >
                                {plateAccessSaving[m.id] ? 'Enregistrement...' : 'Enregistrer'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {m.is_admin && user?.is_super_admin && (
                      <button
                        className="btn-revoke"
                        onClick={() => setRevokeAdminTarget({ id: m.id, email: m.email, name: displayName })}
                      >
                        Révoquer admin
                      </button>
                    )}
                    <button
                      className="btn-revoke"
                      style={{background:'#f1f5f9',color:'#475569'}}
                      onClick={() => handleRemoveMember(m.id)}
                      title="Retirer de l'entreprise"
                    >
                      Retirer
                    </button>
                  </div>
                </div>
              );
            })}
            {invitations.map(inv => (
              <div key={`inv-${inv.id}`} className="admin-row">
                <div className="admin-avatar">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(inv.email.split('@')[0])}&background=e5e7eb&color=6b7280&bold=true`}
                    alt="Avatar"
                  />
                </div>
                <div className="admin-info">
                  <div className="admin-name">{inv.email}</div>
                  <div className="admin-email">Expire le {formatDate(inv.expires_at)}</div>
                </div>
                <div className="admin-meta">
                  <span className="admin-badge" style={{background:'#f1f5f9',color:'#64748b'}}>En attente</span>
                </div>
                <div className="admin-actions">
                  <button
                    className="btn-revoke"
                    style={{background:'#f1f5f9',color:'#475569'}}
                    onClick={() => handleCancelInvitation(inv.id)}
                    title="Annuler l'invitation"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ))}
            {members.length === 0 && invitations.length === 0 && (
              <div className="empty-state" style={{padding:'2rem 0'}}>Aucun membre pour cette entreprise</div>
            )}
          </div>
        )}
        </React.Fragment>}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPlate ? 'Éditer la plaque NFC' : 'Nouvelle plaque NFC'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#000" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreatePlate}>
              <div className="form-group">
                <label>Nom de la plaque</label>
                <input
                  type="text"
                  value={newPlate.name}
                  onChange={(e) => setNewPlate({ ...newPlate, name: e.target.value })}
                  placeholder="Ex: Plaque d'entrée"
                  required
                />
              </div>
              <div className="form-group">
                <label>URL Google Avis</label>
                <input
                  type="url"
                  value={newPlate.google_review_url}
                  onChange={(e) => setNewPlate({ ...newPlate, google_review_url: e.target.value })}
                  placeholder="https://g.page/..."
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  {editingPlate ? 'Enregistrer' : 'Créer la plaque'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke Admin Confirmation Modal */}
      {revokeAdminTarget && (
        <div className="modal-overlay" onClick={() => { if (!revokeAdminLoading) { setRevokeAdminTarget(null); setRevokeAdminResult(null); } }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Révoquer les droits admin</h2>
              <button className="modal-close" onClick={() => { setRevokeAdminTarget(null); setRevokeAdminResult(null); }} disabled={revokeAdminLoading}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#000" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <p style={{color:'#555',margin:'0 0 1.5rem',lineHeight:1.6}}>
              Voulez-vous révoquer les droits administrateur de&nbsp;
              <strong>{revokeAdminTarget.name}</strong>&nbsp;?
              <br/>Un email de notification lui sera envoyé.
            </p>
            {revokeAdminResult && (
              <div className={`import-result ${revokeAdminResult.success ? 'import-success' : 'import-error'}`} style={{marginBottom:'1rem'}}>
                <span>{revokeAdminResult.message}</span>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => { setRevokeAdminTarget(null); setRevokeAdminResult(null); }} disabled={revokeAdminLoading}>
                Annuler
              </button>
              <button type="button" className="btn-submit" style={{background:'#fee2e2',color:'#b91c1c'}} onClick={handleRevokeAdmin} disabled={revokeAdminLoading}>
                {revokeAdminLoading ? 'Traitement...' : 'Révoquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => { setShowInviteModal(false); setInviteResult(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Inviter un membre</h2>
              <button className="modal-close" onClick={() => { setShowInviteModal(false); setInviteResult(null); }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#000" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  placeholder="Prénom"
                />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  placeholder="Nom"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="email@exemple.com"
                  required
                />
              </div>
              {inviteResult && (
                <div className={`import-result ${inviteResult.success ? 'import-success' : 'import-error'}`} style={{ marginBottom: '1rem' }}>
                  <span>{inviteResult.message}</span>
                  <button type="button" className="import-result-close" onClick={() => setInviteResult(null)}>×</button>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowInviteModal(false); setInviteResult(null); }}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit" disabled={inviting}>
                  {inviting ? 'Envoi...' : "Envoyer l'invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyDetail;
